import { useMessageTemplates, fillTemplate, HESAP_TEMPLATES } from '../../lib/messageTemplates';
import { WhatsAppButton } from '../../components/hesap/WhatsAppButton';
import { useHesapBranding } from '../../store/hesapBrandingStore';
import { tl } from '../../lib/whatsapp';
import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useState } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, Loader2, Users, ChevronRight, Phone, MapPin, Banknote, ArrowDownLeft, ArrowUpRight, Package, ShoppingBag, ChevronDown, ChevronUp, Edit2, Trash2, FileText, Download, Tag, TrendingDown, FileSignature } from 'lucide-react';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;


export default function HesapCustomersPage() {
  const brand = useHesapBranding(st => st.title);
  const tpl = useMessageTemplates('/api/hesap', useHesapAuth(st => st.token), HESAP_TEMPLATES);
  const debtMessage = (name, debt, firma) => fillTemplate(tpl.get('borc'), { musteri: name, borc: tl(debt), firma });
  const { token } = useHesapAuth();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [form, setForm] = useState({ full_name: '', phone: '', address: '', note: '' });
  const [saving, setSaving] = useState(false);

  // Detail view
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [showTxDialog, setShowTxDialog] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [txForm, setTxForm] = useState({ type: 'borc', amount: '', description: '', items: '', account_label: '' });
  const [savingTx, setSavingTx] = useState(false);
  const [showSalesHistory, setShowSalesHistory] = useState(true);
  const [expandedSale, setExpandedSale] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [activeAccount, setActiveAccount] = useState('__all__'); // '__all__' = tümü, else label
  const [customerTeklifler, setCustomerTeklifler] = useState([]);
  const [tekliflerLoading, setTekliflerLoading] = useState(false);

  const downloadCustomerPdf = async (filterLabel = '') => {
    if (!customerDetail) return;
    setDownloadingPdf(true);
    try {
      const url = filterLabel
        ? `${API}/customers/${customerDetail.id}/report-pdf?account_label=${encodeURIComponent(filterLabel)}`
        : `${API}/customers/${customerDetail.id}/report-pdf`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('PDF alınamadı');
      const blob = await r.blob();
      const cd = r.headers.get('content-disposition') || '';
      const safeLabel = filterLabel ? `_${filterLabel.replace(/[^a-zA-Z0-9_ğıüşöçİĞÜŞÖÇ]+/g, '_')}` : '';
      let fname = `musteri_raporu_${customerDetail.full_name}${safeLabel}.pdf`;
      const m = cd.match(/filename\*=UTF-8''([^;]+)/) || cd.match(/filename="([^"]+)"/);
      if (m) {
        try { fname = decodeURIComponent(m[1]); } catch { fname = m[1]; }
      }
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl; a.download = fname; a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(objUrl); }, 500);
      toast.success(filterLabel ? `PDF indirildi (${filterLabel})` : 'PDF indirildi');
    } catch (e) {
      toast.error('PDF indirilemedi: ' + e.message);
    } finally {
      setDownloadingPdf(false);
    }
  };

  useEffect(() => { loadCustomers(); }, []);

  const loadCustomers = async () => {
    try {
      const res = await fetch(`${API}/customers`, { headers });
      if (res.ok) setCustomers(await res.json());
    } finally { setLoading(false); }
  };

  const loadCustomerDetail = async (id) => {
    const res = await fetch(`${API}/customers/${id}`, { headers });
    if (res.ok) {
      const data = await res.json();
      setCustomerDetail(data);
      setSelectedCustomer(data);
    }
    // Teklifleri paralel olarak yükle
    setTekliflerLoading(true);
    try {
      const tr = await fetch(`${API}/customers/${id}/teklifler`, { headers });
      if (tr.ok) setCustomerTeklifler(await tr.json());
      else setCustomerTeklifler([]);
    } catch (_) { setCustomerTeklifler([]); }
    finally { setTekliflerLoading(false); }
  };

  const downloadTeklifPdf = async (t) => {
    try {
      const r = await fetch(`${API}/teklif/${t.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('PDF alınamadı');
      const blob = await r.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl; a.download = `teklif_${t.teklif_no}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objUrl);
    } catch (e) { toast.error(e.message); }
  };

  const saveCustomer = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) { toast.error('Ad soyad gerekli'); return; }
    setSaving(true);
    try {
      if (editingCustomer) {
        const res = await fetch(`${API}/customers/${editingCustomer.id}`, { method: 'PUT', headers, body: JSON.stringify(form) });
        if (!res.ok) throw new Error();
        toast.success('Müşteri güncellendi');
      } else {
        const res = await fetch(`${API}/customers`, { method: 'POST', headers, body: JSON.stringify(form) });
        if (!res.ok) throw new Error();
        toast.success('Müşteri eklendi');
      }
      setShowDialog(false);
      setEditingCustomer(null);
      setForm({ full_name: '', phone: '', address: '', note: '' });
      loadCustomers();
      if (selectedCustomer && editingCustomer) loadCustomerDetail(editingCustomer.id);
    } catch { toast.error('Kayıt başarısız'); }
    finally { setSaving(false); }
  };

  const deleteCustomer = async (id, name) => {
    if (!window.confirm(`"${name}" müşterisini ve tüm işlemlerini silmek istediğinize emin misiniz?`)) return;
    try {
      const res = await fetch(`${API}/customers/${id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error();
      toast.success('Müşteri silindi');
      setSelectedCustomer(null);
      setCustomerDetail(null);
      loadCustomers();
    } catch { toast.error('Silme başarısız'); }
  };

  const openEditCustomer = (c) => {
    setEditingCustomer(c);
    setForm({ full_name: c.full_name, phone: c.phone || '', address: c.address || '', note: c.note || '' });
    setShowDialog(true);
  };

  const saveTx = async (e) => {
    e.preventDefault();
    if (!txForm.amount) { toast.error('Tutar gerekli'); return; }
    setSavingTx(true);
    try {
      if (editingTx) {
        const res = await fetch(`${API}/transactions/${editingTx.id}`, {
          method: 'PUT', headers,
          body: JSON.stringify({
            amount: parseFloat(txForm.amount),
            type: txForm.type,
            description: txForm.description,
            account_label: (txForm.account_label || '').trim()
          })
        });
        if (!res.ok) throw new Error();
        toast.success('İşlem güncellendi');
      } else {
        const res = await fetch(`${API}/transactions`, {
          method: 'POST', headers,
          body: JSON.stringify({
            customer_id: selectedCustomer.id,
            amount: parseFloat(txForm.amount),
            type: txForm.type,
            description: txForm.description,
            items: txForm.items,
            account_label: (txForm.account_label || '').trim()
          })
        });
        if (!res.ok) throw new Error();
        const typeLabels = { borc: 'Borç', odeme: 'Ödeme', alacak: 'Alacak', emanet_al: 'Emanet alındı', emanet_ver: 'Emanet verildi' };
        toast.success(`${typeLabels[txForm.type]} kaydedildi`);
      }
      setShowTxDialog(false);
      setEditingTx(null);
      setTxForm({ type: 'borc', amount: '', description: '', items: '', account_label: activeAccount !== '__all__' ? activeAccount : '' });
      loadCustomerDetail(selectedCustomer.id);
      loadCustomers();
    } catch { toast.error('Kayıt başarısız'); }
    finally { setSavingTx(false); }
  };

  const deleteTx = async (txId) => {
    if (!window.confirm('Bu işlemi silmek istediğinize emin misiniz?')) return;
    try {
      const res = await fetch(`${API}/transactions/${txId}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error();
      toast.success('İşlem silindi');
      loadCustomerDetail(selectedCustomer.id);
      loadCustomers();
    } catch { toast.error('Silme başarısız'); }
  };

  const openEditTx = (tx) => {
    setEditingTx(tx);
    setTxForm({ type: tx.type, amount: tx.amount, description: tx.description || '', items: '', account_label: tx.account_label || '' });
    setShowTxDialog(true);
  };

  const filtered = customers
    .filter(c => c.full_name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'tr'));

  // Müşteri ID kısaltma
  const shortId = (id) => id ? '#' + String(id).split('-')[0].toUpperCase() : '';
  const txTypeLabels = { borc: 'Borç', odeme: 'Ödeme', alacak: 'Alacak', emanet_al: 'Emanet Alındı', emanet_ver: 'Emanet Verildi', satis: 'Satış' };
  const txTypeColors = { borc: 'text-red-600', odeme: 'text-green-600', alacak: 'text-blue-600', emanet_al: 'text-purple-600', emanet_ver: 'text-orange-600', satis: 'text-blue-600' };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  // Detail view
  if (selectedCustomer && customerDetail) {
    return (
      <div className="space-y-4" data-testid="customer-detail">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => { setSelectedCustomer(null); setCustomerDetail(null); }}>
              &larr; Geri
            </Button>
            <div className="min-w-0">
              <h1 className="text-xl font-bold truncate">{customerDetail.full_name}</h1>
              <div className="flex items-center gap-2 text-[11px] text-slate-500 flex-wrap">
                <span className="font-mono px-1.5 py-0.5 bg-slate-100 rounded">#{String(customerDetail.id || '').split('-')[0].toUpperCase()}</span>
                {customerDetail.created_at && (
                  <span>Kayıt: {new Date(customerDetail.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <Button size="sm" onClick={() => downloadCustomerPdf('')} disabled={downloadingPdf}
              className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="download-customer-pdf-btn">
              {downloadingPdf ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileText className="h-3.5 w-3.5 mr-1" />}
              PDF Rapor (Tümü)
            </Button>
            {activeAccount !== '__all__' && customerDetail.accounts && customerDetail.accounts.length > 1 && (
              <Button size="sm" onClick={() => downloadCustomerPdf(activeAccount)} disabled={downloadingPdf}
                className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="download-account-pdf-btn">
                {downloadingPdf ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileText className="h-3.5 w-3.5 mr-1" />}
                PDF ({activeAccount})
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => openEditCustomer(customerDetail)} data-testid="edit-customer-btn">
              <Edit2 className="h-3.5 w-3.5 mr-1" />Düzenle
            </Button>
            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => deleteCustomer(customerDetail.id, customerDetail.full_name)} data-testid="delete-customer-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1" />Sil
            </Button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500">Toplam Borç</p>
              <p className="text-2xl font-bold text-red-600">
                {(((customerDetail.debt_breakdown?.sale_debt || 0) + (customerDetail.debt_breakdown?.manual_debt || 0))).toLocaleString('tr-TR')} TL
              </p>
              {customerDetail.debt_breakdown && (
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Satış: {(customerDetail.debt_breakdown.sale_debt || 0).toLocaleString('tr-TR')} · İşlem: {(customerDetail.debt_breakdown.manual_debt || 0).toLocaleString('tr-TR')}
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500">Toplam Ödeme</p>
              <p className="text-2xl font-bold text-green-600">{(customerDetail.total_paid || 0).toLocaleString('tr-TR')} TL</p>
              {customerDetail.debt_breakdown?.sale_paid_in_cash > 0 && (
                <p className="text-[10px] text-slate-400 mt-0.5">
                  + {customerDetail.debt_breakdown.sale_paid_in_cash.toLocaleString('tr-TR')} TL satış içi nakit
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500">Bakiye</p>
              <p className={`text-2xl font-bold ${customerDetail.total_debt > 0 ? 'text-red-600' : customerDetail.total_debt < 0 ? 'text-green-600' : 'text-slate-500'}`}>
                {(customerDetail.total_debt || 0).toLocaleString('tr-TR')} TL
              </p>
              <p className="text-[10px] text-slate-400 mt-0.5">
                {customerDetail.total_debt > 0 ? 'Müşteri borçlu' : customerDetail.total_debt < 0 ? 'Müşteri alacaklı' : 'Kapalı'}
              </p>
              {customerDetail.total_debt > 0 && (customerDetail.phone || customerDetail.customer?.phone) && (
                <WhatsAppButton className="mt-2" label="Borç Hatırlat" testId="customer-detail-wa-btn"
                  phone={customerDetail.phone || customerDetail.customer?.phone}
                  text={() => debtMessage(customerDetail.full_name || customerDetail.customer?.full_name, customerDetail.total_debt, brand)} />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Alışveriş Özet Kartı - tüm satışlar (peşin + veresiye) */}
        {customerDetail.stats && customerDetail.stats.sales_count > 0 && (
          <Card className="bg-gradient-to-br from-blue-50 to-emerald-50 border-blue-200" data-testid="customer-stats-card">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="text-center">
                  <div className="flex justify-center items-center gap-1 text-blue-700">
                    <ShoppingBag className="h-4 w-4" />
                    <p className="text-xs font-medium">Satış</p>
                  </div>
                  <p className="text-xl font-bold text-blue-700 mt-0.5">{customerDetail.stats.sales_count}</p>
                </div>
                <div className="text-center">
                  <div className="flex justify-center items-center gap-1 text-purple-700">
                    <Package className="h-4 w-4" />
                    <p className="text-xs font-medium">Ürün Adedi</p>
                  </div>
                  <p className="text-xl font-bold text-purple-700 mt-0.5">{customerDetail.stats.total_items}</p>
                </div>
                <div className="text-center">
                  <div className="flex justify-center items-center gap-1 text-red-600">
                    <TrendingDown className="h-4 w-4" />
                    <p className="text-xs font-medium">Toplam İndirim</p>
                  </div>
                  <p className="text-xl font-bold text-red-600 mt-0.5">
                    {(customerDetail.stats.total_discount || 0).toLocaleString('tr-TR')} TL
                  </p>
                </div>
                <div className="text-center">
                  <div className="flex justify-center items-center gap-1 text-emerald-700">
                    <Banknote className="h-4 w-4" />
                    <p className="text-xs font-medium">Toplam Alışveriş</p>
                  </div>
                  <p className="text-xl font-bold text-emerald-700 mt-0.5">
                    {(customerDetail.stats.total_purchases || 0).toLocaleString('tr-TR')} TL
                  </p>
                  {customerDetail.stats.total_manual_debt > 0 && (
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      + {customerDetail.stats.total_manual_debt.toLocaleString('tr-TR')} TL manuel borç
                      <br />
                      <span className="font-bold text-slate-700 dark:text-slate-200">
                        = {(customerDetail.stats.total_liability || 0).toLocaleString('tr-TR')} TL toplam
                      </span>
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {customerDetail.phone && (
          <p className="text-sm text-slate-500 flex items-center gap-1"><Phone className="h-3 w-3" />{customerDetail.phone}</p>
        )}
        {customerDetail.address && (
          <p className="text-sm text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{customerDetail.address}</p>
        )}

        {/* Emanet items */}
        {customerDetail.emanet_items?.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="font-medium mb-2 flex items-center gap-2"><Package className="h-4 w-4" />Emanetler</h3>
              <div className="space-y-1">
                {customerDetail.emanet_items.map((e, i) => (
                  <div key={i} className="flex justify-between text-sm p-2 bg-slate-50 rounded">
                    <span>{e.type === 'emanet_al' ? 'Alınan' : 'Verilen'}: {e.description}</span>
                    <span className="font-medium">{e.amount.toLocaleString('tr-TR')} TL</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sales History — tüm satışlar (peşin + veresiye) */}
        {(() => {
          const allSales = customerDetail.sales || [];
          if (allSales.length === 0) return null;
          const pmLabels = { nakit: 'Nakit', kart: 'Kart', havale: 'Havale', veresiye: 'Veresiye' };
          return (
            <Card data-testid="sales-history-section">
              <CardContent className="p-4">
                <button onClick={() => setShowSalesHistory(!showSalesHistory)}
                  className="w-full flex items-center justify-between mb-2">
                  <h3 className="font-medium flex items-center gap-2">
                    <ShoppingBag className="h-4 w-4 text-blue-600" />Satış Geçmişi ({allSales.length})
                  </h3>
                  {showSalesHistory ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                {showSalesHistory && (
                  <div className="space-y-2">
                    {allSales.map(s => (
                      <div key={s.id} className="border rounded-lg overflow-hidden">
                        <button onClick={() => setExpandedSale(expandedSale === s.id ? null : s.id)}
                          className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 text-left">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <ShoppingBag className="h-4 w-4 text-blue-500 flex-shrink-0" />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-medium">
                                  {(s.items || []).slice(0, 2).map(i => `${i.product_name} x${i.quantity}`).join(', ')}
                                  {s.items?.length > 2 && ` +${s.items.length - 2} ürün`}
                                </p>
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold">
                                  {pmLabels[s.payment_method] || s.payment_method}
                                </span>
                                {s.is_veresiye && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold">
                                    Veresiye
                                  </span>
                                )}
                                {s.discount > 0 && (
                                  <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-bold">
                                    <Tag className="h-2.5 w-2.5" />İndirim {s.discount.toLocaleString('tr-TR')} TL
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-400">{new Date(s.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                            </div>
                          </div>
                          <div className="text-right flex items-center gap-2">
                            <div>
                              <p className="font-bold text-emerald-700">{(s.net_amount || 0).toLocaleString('tr-TR')} TL</p>
                              {s.discount > 0 && (
                                <p className="text-[10px] text-slate-400 line-through">
                                  {(s.total_amount || 0).toLocaleString('tr-TR')} TL
                                </p>
                              )}
                              {s.is_veresiye && s.remaining_debt > 0 && (
                                <p className="text-xs text-red-500">Kalan: {s.remaining_debt.toLocaleString('tr-TR')} TL</p>
                              )}
                            </div>
                            {expandedSale === s.id ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                          </div>
                        </button>
                        {expandedSale === s.id && (
                          <div className="border-t">
                            {s.items && s.items.length > 0 && (
                              <table className="w-full text-sm">
                                <thead className="bg-slate-50">
                                  <tr>
                                    <th className="text-left p-2 pl-3 font-medium text-slate-500">Ürün</th>
                                    <th className="text-center p-2 font-medium text-slate-500">Adet</th>
                                    <th className="text-right p-2 font-medium text-slate-500">B.Fiyat</th>
                                    <th className="text-right p-2 pr-3 font-medium text-slate-500">Toplam</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {s.items.map((item, idx) => (
                                    <tr key={idx} className="border-t">
                                      <td className="p-2 pl-3">{item.product_name}</td>
                                      <td className="p-2 text-center">{item.quantity}</td>
                                      <td className="p-2 text-right">{(item.unit_price || 0).toLocaleString('tr-TR')} TL</td>
                                      <td className="p-2 pr-3 text-right font-medium">{((item.quantity || 0) * (item.unit_price || 0)).toLocaleString('tr-TR')} TL</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                            <div className="border-t p-2 px-3 bg-slate-50 space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-500">Brüt Toplam:</span>
                                <span className="font-medium">{(s.total_amount || 0).toLocaleString('tr-TR')} TL</span>
                              </div>
                              {s.discount > 0 && (
                                <div className="flex justify-between text-red-600">
                                  <span className="flex items-center gap-1"><Tag className="h-3 w-3" />İndirim:</span>
                                  <span className="font-medium">-{s.discount.toLocaleString('tr-TR')} TL</span>
                                </div>
                              )}
                              <div className="flex justify-between border-t pt-1">
                                <span className="text-slate-700 font-bold">Net Tutar:</span>
                                <span className="font-bold text-emerald-700">{(s.net_amount || 0).toLocaleString('tr-TR')} TL</span>
                              </div>
                              {s.is_veresiye && (
                                <>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Ödenen:</span>
                                    <span className="text-emerald-600 font-medium">{(s.paid_amount || 0).toLocaleString('tr-TR')} TL</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Kalan Borç:</span>
                                    <span className={`font-medium ${s.remaining_debt > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                      {(s.remaining_debt || 0).toLocaleString('tr-TR')} TL
                                    </span>
                                  </div>
                                </>
                              )}
                              {s.note && (
                                <div className="pt-1 border-t italic text-slate-500 text-xs">Not: {s.note}</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* Alt-Hesap Tab'ları */}
        {customerDetail.accounts && customerDetail.accounts.length > 1 && (
          <div className="bg-white dark:bg-slate-900 border rounded-lg p-3" data-testid="accounts-tabs">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Hesap Kategorileri</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveAccount('__all__')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${activeAccount === '__all__' ? 'bg-slate-800 dark:bg-slate-100 text-white dark:text-slate-800' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200'}`}
                data-testid="account-tab-all"
              >
                Tümü <span className="opacity-70">({customerDetail.transactions?.filter(t => t.type !== 'satis').length || 0})</span>
              </button>
              {customerDetail.accounts.map(a => (
                <button
                  key={a.label}
                  onClick={() => setActiveAccount(a.label)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${activeAccount === a.label ? 'bg-sky-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200'}`}
                  data-testid={`account-tab-${a.label}`}
                >
                  {a.label} <span className={`ml-1 ${activeAccount === a.label ? 'opacity-80' : 'opacity-60'}`}>· {a.balance > 0 ? `${a.balance.toLocaleString('tr-TR')} TL borç` : a.balance < 0 ? `${Math.abs(a.balance).toLocaleString('tr-TR')} TL alacak` : 'kapalı'}</span>
                </button>
              ))}
            </div>
            {activeAccount !== '__all__' && (
              <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                {(() => {
                  const acc = customerDetail.accounts.find(a => a.label === activeAccount);
                  if (!acc) return null;
                  return (
                    <>
                      <div className="bg-red-50 dark:bg-red-900/20 rounded p-2">
                        <p className="text-slate-500">Bu kategoride borç</p>
                        <p className="font-bold text-red-600">{acc.debt.toLocaleString('tr-TR')} TL</p>
                      </div>
                      <div className="bg-green-50 dark:bg-green-900/20 rounded p-2">
                        <p className="text-slate-500">Bu kategoride ödenen</p>
                        <p className="font-bold text-green-600">{acc.paid.toLocaleString('tr-TR')} TL</p>
                      </div>
                      <div className={`rounded p-2 ${acc.balance > 0 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-green-100 dark:bg-green-900/30'}`}>
                        <p className="text-slate-500">Bakiye</p>
                        <p className={`font-bold ${acc.balance > 0 ? 'text-red-700' : 'text-green-700'}`}>
                          {acc.balance > 0 ? `${acc.balance.toLocaleString('tr-TR')} TL Borç` : acc.balance < 0 ? `${Math.abs(acc.balance).toLocaleString('tr-TR')} TL Alacak` : 'Kapalı'}
                        </p>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* Teklifler Bölümü */}
        <div className="bg-white dark:bg-slate-900 border rounded-lg" data-testid="customer-teklifler">
          <div className="flex items-center justify-between p-3 border-b">
            <h3 className="font-medium flex items-center gap-2">
              <FileSignature className="h-4 w-4 text-emerald-600" />
              Teklifler
              {customerTeklifler.length > 0 && (
                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">{customerTeklifler.length}</span>
              )}
            </h3>
          </div>
          {tekliflerLoading ? (
            <div className="p-4 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          ) : customerTeklifler.length === 0 ? (
            <p className="p-4 text-center text-xs text-slate-400">
              Bu müşteri için teklif yok. Teklif Formu'ndan yeni teklif oluşturabilirsiniz.
            </p>
          ) : (
            <div className="divide-y">
              {customerTeklifler.map(t => {
                const stMeta = {
                  beklemede: { label: 'Beklemede', color: 'bg-yellow-100 text-yellow-700' },
                  onaylandi: { label: 'Onaylandı', color: 'bg-emerald-100 text-emerald-700' },
                  reddedildi: { label: 'Reddedildi', color: 'bg-red-100 text-red-700' },
                }[t.status] || { label: t.status || 'Beklemede', color: 'bg-slate-100 text-slate-700' };
                return (
                  <div key={t.id} className="p-3 flex items-center justify-between gap-2 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs font-semibold text-emerald-700 dark:text-emerald-400">{t.teklif_no}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${stMeta.color}`}>{stMeta.label}</span>
                        <span className="text-[10px] text-slate-500">
                          {t.date ? new Date(t.date).toLocaleDateString('tr-TR') : '-'}
                        </span>
                      </div>
                      {t.items?.length > 0 && (
                        <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                          {t.items.slice(0, 2).map(i => i.name).filter(Boolean).join(', ')}
                          {t.items.length > 2 ? ` +${t.items.length - 2} kalem` : ''}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-slate-800 dark:text-slate-100 tabular-nums">
                        {Number(t.total || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                      </span>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => downloadTeklifPdf(t)} title="PDF">
                        <Download className="h-4 w-4 text-blue-600" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>


        <div className="flex items-center justify-between">
          <h3 className="font-medium">
            İşlem Geçmişi
            {activeAccount !== '__all__' && (
              <span className="text-xs text-slate-500 ml-2">— {activeAccount}</span>
            )}
          </h3>
          <Button size="sm" onClick={() => {
            setTxForm(prev => ({ ...prev, account_label: activeAccount !== '__all__' ? activeAccount : '' }));
            setShowTxDialog(true);
          }} data-testid="add-transaction-btn">
            <Plus className="h-4 w-4 mr-1" />İşlem Ekle
          </Button>
        </div>

        <div className="space-y-2">
          {(customerDetail.transactions || [])
            .filter(tx => tx.type !== 'satis')
            .filter(tx => activeAccount === '__all__' || (tx.account_label || '') === (activeAccount === 'Ana Hesap' ? '' : activeAccount))
            .map(tx => (
            <div key={tx.id} className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 rounded-lg border">
              <div className="flex items-center gap-3">
                {tx.type === 'odeme' ? <ArrowDownLeft className="h-4 w-4 text-green-500" /> : <ArrowUpRight className="h-4 w-4 text-red-500" />}
                <div>
                  <p className={`text-sm font-medium ${txTypeColors[tx.type]}`}>
                    {txTypeLabels[tx.type]}
                    {tx.account_label && (
                      <span className="ml-2 text-[10px] bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 px-2 py-0.5 rounded-full font-medium">
                        {tx.account_label}
                      </span>
                    )}
                  </p>
                  {tx.description && <p className="text-xs text-slate-500">{tx.description}</p>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <p className="font-medium">{tx.amount.toLocaleString('tr-TR')} TL</p>
                  <p className="text-xs text-slate-400">{new Date(tx.created_at).toLocaleDateString('tr-TR')}</p>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditTx(tx)} title="Düzenle">
                  <Edit2 className="h-3 w-3 text-slate-400" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteTx(tx.id)} title="Sil">
                  <Trash2 className="h-3 w-3 text-red-400" />
                </Button>
              </div>
            </div>
          ))}
          {(!customerDetail.transactions || customerDetail.transactions.filter(tx => tx.type !== 'satis').filter(tx => activeAccount === '__all__' || (tx.account_label || '') === (activeAccount === 'Ana Hesap' ? '' : activeAccount)).length === 0) && (
            <p className="text-center text-slate-400 py-4">Bu kategoride henüz işlem yok</p>
          )}
        </div>

        {/* Data list for known account labels (autocomplete) */}
        <datalist id="account-labels-list">
          {(customerDetail.accounts || []).filter(a => a.label !== 'Ana Hesap').map(a => (
            <option key={a.label} value={a.label} />
          ))}
        </datalist>

        <Dialog open={showTxDialog} onOpenChange={(v) => { setShowTxDialog(v); if (!v) setEditingTx(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>{editingTx ? 'İşlem Düzenle' : 'Yeni İşlem'} - {selectedCustomer.full_name}</DialogTitle></DialogHeader>
            <form onSubmit={saveTx} className="space-y-3">
              <div className="space-y-1">
                <Label>İşlem Tipi</Label>
                <select value={txForm.type} onChange={(e) => setTxForm({...txForm, type: e.target.value})}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                  <option value="borc">Borç Ekle</option>
                  <option value="odeme">Ödeme Al</option>
                  <option value="alacak">Alacak (Borç Düş)</option>
                  <option value="emanet_al">Emanet Al</option>
                  <option value="emanet_ver">Emanet Ver</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label>Hesap Kategorisi <span className="text-xs text-slate-400">(boş = ana hesap)</span></Label>
                <Input
                  value={txForm.account_label}
                  onChange={(e) => setTxForm({...txForm, account_label: e.target.value})}
                  placeholder="Ör: İnşaat İşi, Nakliye vs."
                  list="account-labels-list"
                  data-testid="tx-account-label-input"
                />
                <p className="text-[10px] text-slate-400">Aynı müşteride birbirinden ayrı işleri gruplamak için kullanın.</p>
              </div>
              <div className="space-y-1">
                <Label>Tutar (TL) *</Label>
                <Input type="number" step="0.01" value={txForm.amount}
                  onChange={(e) => setTxForm({...txForm, amount: e.target.value})} />
              </div>
              <div className="space-y-1">
                <Label>Açıklama</Label>
                <Input value={txForm.description} onChange={(e) => setTxForm({...txForm, description: e.target.value})}
                  placeholder="Ne için?" />
              </div>
              {txForm.type.startsWith('emanet') && (
                <div className="space-y-1">
                  <Label>Emanet Detayı</Label>
                  <Input value={txForm.items} onChange={(e) => setTxForm({...txForm, items: e.target.value})}
                    placeholder="Emanet edilen şeyler..." />
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowTxDialog(false)} className="flex-1">İptal</Button>
                <Button type="submit" disabled={savingTx} className="flex-1">
                  {savingTx ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-4" data-testid="hesap-customers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-800">Müşteriler / Veresiye ({customers.length})</h1>
        <Button onClick={() => setShowDialog(true)} data-testid="add-customer-btn">
          <Plus className="h-4 w-4 mr-1" />Yeni Müşteri
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input placeholder="Müşteri ara..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
      </div>

      {filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500">{search ? 'Sonuç bulunamadı' : 'Henüz müşteri yok'}</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(c => (
            <Card key={c.id} className="hover:shadow-md transition-shadow group cursor-pointer" onClick={() => loadCustomerDetail(c.id)}>
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium">{c.full_name}</p>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">{shortId(c.id)}</span>
                  </div>
                  {c.phone && <p className="text-xs text-slate-500">{c.phone}</p>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {(c.total_debt || 0) !== 0 && (
                    <span className={`font-bold ${c.total_debt > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{(c.total_debt || 0).toLocaleString('tr-TR')} TL</span>
                  )}
                  {c.phone && c.total_debt > 0 && (
                    <WhatsAppButton compact phone={c.phone} text={() => debtMessage(c.full_name, c.total_debt, brand)} label="Borç hatırlatma gönder" testId={`customer-wa-${c.id}`} />
                  )}
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={(v) => { setShowDialog(v); if (!v) { setEditingCustomer(null); setForm({ full_name: '', phone: '', address: '', note: '' }); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingCustomer ? 'Müşteri Düzenle' : 'Yeni Müşteri'}</DialogTitle></DialogHeader>
          <form onSubmit={saveCustomer} className="space-y-3">
            <div className="space-y-1"><Label>Ad Soyad *</Label>
              <Input value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} data-testid="customer-name-input" /></div>
            <div className="space-y-1"><Label>Telefon</Label>
              <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
            <div className="space-y-1"><Label>Adres</Label>
              <Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} /></div>
            <div className="space-y-1"><Label>Not</Label>
              <Input value={form.note} onChange={(e) => setForm({...form, note: e.target.value})} /></div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="flex-1">İptal</Button>
              <Button type="submit" disabled={saving} className="flex-1">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
